import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface ProcessingStep {
  label: string;
  description: string;
  icon: string;
  progressStart: number;
  progressEnd: number;
}

interface ProcessingOverlayProps {
  isVisible: boolean;
  progress?: number;
  currentStep?: number;
  totalSteps?: number;
}

const processingSteps: ProcessingStep[] = [
  {
    label: 'Creating your profile',
    description: 'Setting up your account and initial profile',
    icon: '👤',
    progressStart: 0,
    progressEnd: 20,
  },
  {
    label: 'Analyzing your responses',
    description: 'Understanding your career goals and preferences',
    icon: '🔍',
    progressStart: 20,
    progressEnd: 40,
  },
  {
    label: 'Generating your AI-powered bio',
    description: 'AI is crafting your personalized professional summary',
    icon: '✨',
    progressStart: 40,
    progressEnd: 70,
  },
  {
    label: 'Building your personalized learning path',
    description: 'Designing your customized career journey',
    icon: '🎯',
    progressStart: 70,
    progressEnd: 90,
  },
  {
    label: 'Almost there! Finalizing your profile',
    description: 'Putting everything together for you',
    icon: '🚀',
    progressStart: 90,
    progressEnd: 100,
  },
];

export const ProcessingOverlay: React.FC<ProcessingOverlayProps> = ({
  isVisible,
  progress = 0,
  currentStep: _currentStep = 0,
  totalSteps: _totalSteps = 5,
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setAnimatedProgress(0);
      return;
    }
    
    // Animate progress smoothly
    const interval = setInterval(() => {
      setAnimatedProgress((prev) => {
        const target = progress;
        const diff = target - prev;
        if (Math.abs(diff) < 0.5) return target;
        return prev + diff * 0.15; // Smooth easing
      });
    }, 50);

    return () => clearInterval(interval);
  }, [isVisible, progress]);

  if (!isVisible) return null;

  // Determine active step based on progress
  const activeStepIndex = processingSteps.findIndex(
    (step) => animatedProgress >= step.progressStart && animatedProgress < step.progressEnd
  );
  const activeStep = activeStepIndex >= 0 ? activeStepIndex : processingSteps.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop with blur and gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-secondary/20 backdrop-blur-md" />
      
      {/* Content */}
      <div className="relative w-full max-w-2xl mx-auto animate-scale-in">
        <div className="relative rounded-3xl border border-border/50 bg-card/95 backdrop-blur-xl shadow-2xl overflow-hidden p-8 sm:p-12">
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 animate-pulse" />
          
          <div className="relative space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/20 mb-4 animate-pulse">
                <span className="text-4xl">
                  {processingSteps[activeStep]?.icon || '⚡'}
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                Creating Your Personalized Plan
              </h2>
              <p className="text-muted-foreground text-base">
                {processingSteps[activeStep]?.description || 'This will only take a moment...'}
              </p>
            </div>

            {/* Progress Steps */}
            <div className="space-y-3">
              {processingSteps.map((step, index) => {
                const isActive = index === activeStep;
                const isCompleted = animatedProgress > step.progressEnd;
                const isUpcoming = animatedProgress < step.progressStart;

                // Calculate progress within this step
                const stepProgress = isActive
                  ? Math.max(0, Math.min(100, ((animatedProgress - step.progressStart) / (step.progressEnd - step.progressStart)) * 100))
                  : isCompleted
                  ? 100
                  : 0;

                return (
                  <div
                    key={index}
                    className={cn(
                      'flex items-start gap-4 p-4 rounded-xl border-2 transition-all duration-500',
                      isActive
                        ? 'border-primary bg-primary/10 shadow-lg scale-[1.02]'
                        : isCompleted
                        ? 'border-primary/30 bg-primary/5'
                        : 'border-muted/30 bg-muted/20 opacity-60'
                    )}
                  >
                    <div
                      className={cn(
                        'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all relative overflow-hidden',
                        isActive && 'bg-primary text-primary-foreground animate-pulse',
                        isCompleted && 'bg-primary text-primary-foreground',
                        isUpcoming && 'bg-muted text-muted-foreground'
                      )}
                    >
                      {isCompleted ? '✓' : isActive ? step.icon : String(index + 1)}
                      {isActive && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <p
                          className={cn(
                            'font-semibold transition-colors',
                            isActive ? 'text-foreground' : 'text-muted-foreground'
                          )}
                        >
                          {step.label}
                        </p>
                        {isActive && (
                          <span className="text-xs text-primary font-medium">
                            {Math.round(stepProgress)}%
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                      {isActive && (
                        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-300"
                            style={{ width: `${stepProgress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Overall Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground font-medium">Overall Progress</span>
                <span className="text-primary font-semibold">{Math.round(animatedProgress)}%</span>
              </div>
              <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-primary via-primary to-secondary transition-all duration-500 ease-out"
                  style={{ width: `${animatedProgress}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                </div>
              </div>
            </div>

            {/* Animated dots */}
            <div className="flex justify-center gap-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-primary animate-pulse"
                  style={{
                    animationDelay: `${i * 0.2}s`,
                    animationDuration: '1s',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
